import React from 'react';
import { AlertTriangle, UserPlus } from 'lucide-react';
import { Button } from '../Button';
import { Ampel, Kapazitaet, KapazitaetRegion } from '../../services/erpSteering';
import { KapazitaetEinstellungen } from '../../hooks/useErpCockpit';
import { KartenRahmen } from './KartenRahmen';

// Kapazitaetsplanung: Begehungs-Soll je Monat gegen verfuegbare Koepfe
// (ERP/CRM-Plan Abschnitt 8, „Q4 ueberbucht in Bayern → Freelancer
// akquirieren").
//
// Das Soll wird aus dem Begehungsturnus des Projekts abgeleitet — die Annahme
// ist in erpSteering.ts::begehungenProMonat ausgeschrieben. Die beiden
// Kapazitaetswerte sind Vorgaben, keine Messwerte: sie stehen deshalb als
// Eingabefelder hier und nicht versteckt in einer Konstanten.

interface Props {
    zustand: { daten: Kapazitaet | null; laedt: boolean; fehler: string | null };
    einstellungen: KapazitaetEinstellungen;
    onEinstellungen: (e: KapazitaetEinstellungen) => void;
    onAnwenden: () => void;
    warnungen: string[];
}

const AMPEL_KLASSEN: Record<Ampel, string> = {
    gruen: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    gelb: 'bg-amber-100 text-amber-800 border-amber-300',
    rot: 'bg-red-100 text-red-800 border-red-300',
};

const AMPEL_LABELS: Record<Ampel, string> = {
    gruen: 'eigene Koordinatoren reichen',
    gelb: 'nur mit Freelancern zu schaffen',
    rot: 'Kapazität reicht nicht',
};

const inputClass =
    'w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

const rund = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 1 });

const monatsLabel = (monat: string): string => {
    const [jahr, m] = monat.split('-');
    const namen = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${namen[Number(m) - 1] ?? m} ${jahr}`;
};

const RegionZeile: React.FC<{ r: KapazitaetRegion }> = ({ r }) => (
    <li className="flex items-center justify-between gap-2 text-xs">
        <span className={`rounded-full border px-2 py-0.5 font-semibold ${AMPEL_KLASSEN[r.ampel]}`}>{r.region}</span>
        <span className="tabular-nums text-slate-500">
            {rund(r.internNoetig)} / {rund(r.kapazitaetIntern)}
            {r.ampel !== 'gruen' && ` (+${rund(r.kapazitaetFreelancer)} FL)`}
        </span>
    </li>
);

export const KapazitaetCard: React.FC<Props> = ({
    zustand,
    einstellungen,
    onEinstellungen,
    onAnwenden,
    warnungen,
}) => {
    const d = zustand.daten;
    const roteMonate = d?.monate.filter((m) => m.ampel === 'rot') ?? [];

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className={labelClass} htmlFor="kap-monate">Monate</label>
                    <input
                        id="kap-monate"
                        type="number"
                        min={1}
                        max={36}
                        className={`${inputClass} block`}
                        value={einstellungen.monate}
                        onChange={(e) => onEinstellungen({ ...einstellungen, monate: Number(e.target.value) || 1 })}
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor="kap-intern">Begehungen / Koordinator</label>
                    <input
                        id="kap-intern"
                        type="number"
                        min={0}
                        className={`${inputClass} block`}
                        value={einstellungen.kapazitaetKoordinator}
                        onChange={(e) =>
                            onEinstellungen({ ...einstellungen, kapazitaetKoordinator: Number(e.target.value) || 0 })
                        }
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor="kap-fl">Begehungen / Freelancer</label>
                    <input
                        id="kap-fl"
                        type="number"
                        min={0}
                        className={`${inputClass} block`}
                        value={einstellungen.kapazitaetFreelancer}
                        onChange={(e) =>
                            onEinstellungen({ ...einstellungen, kapazitaetFreelancer: Number(e.target.value) || 0 })
                        }
                    />
                </div>
                <Button variant="secondary" onClick={onAnwenden}>Neu berechnen</Button>
            </div>

            <KartenRahmen laedt={zustand.laedt} fehler={zustand.fehler}>
                {d && (
                    <div className="space-y-5">
                        {warnungen.length > 0 && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                {warnungen.map((w) => <p key={w}>{w}</p>)}
                            </div>
                        )}

                        <p className="text-xs text-slate-500">
                            Verfügbar: {rund(d.kapazitaetInternGesamt)} Begehungen/Monat intern ·{' '}
                            {rund(d.kapazitaetFreelancerGesamt)} über einsatzbereite Freelancer.
                            {d.projekteOhneIntervall > 0 && (
                                <> {' '}<strong>{d.projekteOhneIntervall}</strong> laufende Projekte haben ein
                                manuelles oder fehlendes Begehungsintervall — ihr Soll lässt sich nicht ableiten und
                                fehlt in dieser Rechnung.</>
                            )}
                        </p>

                        {roteMonate.length > 0 && (
                            <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                <UserPlus className="mt-0.5 h-4 w-4 shrink-0" />
                                <p>
                                    <strong>Freelancer akquirieren.</strong> In {roteMonate.length} Monat(en) reichen
                                    eigene Koordinatoren und einsatzbereite Freelancer zusammen nicht:{' '}
                                    {roteMonate.map((m) => monatsLabel(m.monat)).join(', ')}.
                                </p>
                            </div>
                        )}

                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {d.monate.map((m) => (
                                <div key={m.monat} className={`rounded-xl border p-3 ${AMPEL_KLASSEN[m.ampel]}`}>
                                    <div className="flex items-baseline justify-between">
                                        <p className="text-sm font-bold">{monatsLabel(m.monat)}</p>
                                        <p className="text-xs font-semibold">{AMPEL_LABELS[m.ampel]}</p>
                                    </div>
                                    <p className="mt-1 text-xs tabular-nums">
                                        Soll {rund(m.sollBegehungen)} · vergeben {rund(m.vergebenBegehungen)} ·
                                        intern nötig <strong>{rund(m.internNoetig)}</strong> von{' '}
                                        {rund(m.kapazitaetIntern)}
                                    </p>
                                    {m.fehlendeBegehungen > 0 && (
                                        <p className="mt-1 flex items-center gap-1 text-xs font-bold">
                                            <AlertTriangle className="h-3.5 w-3.5" />
                                            {rund(m.fehlendeBegehungen)} Begehungen ohne Kopf
                                        </p>
                                    )}
                                    {m.regionen.length > 0 && (
                                        <ul className="mt-2 space-y-1 border-t border-black/10 pt-2">
                                            {m.regionen.slice(0, 5).map((r) => (
                                                <RegionZeile key={r.region} r={r} />
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </KartenRahmen>
        </div>
    );
};
